import { useAppSelector } from '@/redux'
import { analytics } from '@/services/analytics/segment'
import { useEffect, useMemo } from 'react'
import { useFirebaseUser } from './useFirebaseUser'
import { useProfile } from './useProfile'

export function useAuthState() {
  const firebaseUser = useFirebaseUser()
  const profile = useProfile()

  const profileMatchesUser = useMemo(() => {
    if (!profile.data) return false

    const hasMatchingId = String(profile.data.id) === String(firebaseUser?.uid)
    const hasMatchingEmail = String(profile.data.email) === String(firebaseUser?.email)

    return hasMatchingId || hasMatchingEmail
  }, [profile.data, firebaseUser])

  const loginResolved =
    firebaseUser === undefined ? false : !!firebaseUser?.uid ? profileMatchesUser || profile.isError : true

  const authFinished = useAppSelector(store => store.AppStatus.authFinished)
  const initialAuthResolved = useAppSelector(store => store.AppStatus.hasResolvedInitialAuth)

  return {
    isLoggedIn: profileMatchesUser,
    loginResolved,
    authFinished,
    initialAuthResolved
  }
}

export function useAutoLogin() {
  const firebaseUser = useFirebaseUser()
  const profile = useProfile({ enabled: true, track: true })

  const profileMatchesUser = useMemo(() => {
    if (!profile.data) return false

    const hasMatchingId = String(profile.data.id) === String(firebaseUser?.uid)
    const hasMatchingEmail = String(profile.data.email) === String(firebaseUser?.email)

    return hasMatchingId || hasMatchingEmail
  }, [profile.data, firebaseUser])

  // Keyed on the profile id as well as the login flag: without it, switching accounts
  // without an unmount leaves the flag true and the new user is never identified. The
  // client itself drops a repeat identify for the same user, so a flag that flips on a
  // profile refetch costs nothing.
  useEffect(() => {
    if (!!profile.data && profileMatchesUser) {
      analytics.identify({ user: profile.data, isLoggedIn: true })
      analytics.track('logged_in_pageview')
    }
  }, [profileMatchesUser, profile.data?.id])

  return {
    isLoggedIn: profileMatchesUser,
    profile: profile.data
  }
}
